<template>
    <li v-if="$device.small">
        <a
            class="uk-accordion-title"
            href="#"
        >
            <div class="uk-flex uk-flex-between">
                <div>
                    <span>
                        {{ customerFullName }}
                    </span>
                </div>
                <div>
                    <time
                        :datetime="payment.paymentDate"
                        class="uk-text-small uk-text-muted uk-margin-left uk-margin-auto-right"
                    >
                        {{ prettyDate }}
                    </time>
                </div>
                <div>
                    <badge
                        :value="payment.paymentStatus"
                        class="uk-text-nowrap"
                    />
                </div>
            </div>
        </a>
        <div class="uk-accordion-content">
            <div class="uk-flex uk-flex-wrap uk-margin-bottom">
                <div class="uk-width-1-2 uk-margin-small-bottom">
                    <strong>Date</strong>
                    {{ prettyDate }}
                </div>
                <div class="uk-width-1-2 uk-margin-small-bottom">
                    <strong>Payment Type</strong>
                    {{ paymentType }}
                </div>
                <div
                    v-if="isUserAdmin"
                    class="uk-width-1-2 uk-margin-small-bottom"
                >
                    <strong>State</strong>
                    {{ payment.paymentStateTitle }}
                </div>
                <div class="uk-width-1-2 uk-margin-small-bottom">
                    <strong>Job ID</strong>
                    {{ payment.jobNumber }}
                </div>
                <div class="uk-width-1-2 uk-margin-small-bottom">
                    <strong>Amount</strong>
                    ${{ amount }}
                </div>
                <div class="uk-width-1-2 uk-margin-small-bottom">
                    <strong>Paid By</strong>
                    {{ customerFullName }}
                </div>
                <div class="uk-width-1-2 uk-margin-small-bottom">
                    <strong>Payment Method</strong>
                    {{ payment.paymentMethodDisplayTitle }}
                </div>
                <div class="uk-width-1-2 uk-margin-small-bottom">
                    <strong>Received By</strong>
                    {{ receivedBy }}
                </div>
            </div>
            <div class="buttons">
                <button
                    v-if="payment.showReceipt"
                    type="button"
                    class="uk-button uk-button-default uk-button-small uk-margin-right"
                    @click="getReceipt(payment.id)"
                >
                    Receipt
                </button>
                <button
                    type="button"
                    class="uk-button uk-button-default uk-button-small"
                    @click="edit(payment.id)"
                >
                    Edit
                </button>
            </div>
        </div>
    </li>
    <tr v-else>
        <td>
            <time
                :datetime="payment.paymentDate"
            >
                <span class="full">{{ prettyDate }}</span>
                <span class="since uk-display-block uk-text-muted">{{ formattedDate }}</span>
            </time>
        </td>
        <td>
            <div class="uk-text-truncate">
                {{ paymentType }}
            </div>
        </td>
        <td v-if="isUserAdmin">
            {{ payment.paymentStateTitle }}
        </td>
        <td>
            {{ payment.jobNumber }}
        </td>
        <td>
            ${{ amount }}
        </td>
        <td>
            <a
                :href="'mailto:' + customerEmail"
                :title="mailLinkTitle"
            >
                {{ customerFullName }}
            </a>
        </td>
        <td>
            {{ payment.paymentMethodDisplayTitle }}
        </td>
        <td>
            <badge
                :value="payment.paymentStatus"
            />
            <a
                v-if="canEditPaymentStatus"
                @click="changeStatus"
            >
                <span uk-icon="pencil"></span>
            </a>
        </td>
        <td>
            <div class="uk-text-truncate">
                {{ receivedBy }}
            </div>
        </td>
        <td>
            <div class="buttons">
                <button
                    v-if="payment.showReceipt"
                    type="button"
                    class="uk-button uk-button-default uk-button-small uk-margin-right"
                    @click="getReceipt(payment.id)"
                >
                    Receipt
                </button>
                <button
                    v-if="payment.paymentStatus === 'payment-link-sent'"
                    type="button"
                    class="uk-button uk-button-default uk-button-small uk-margin-right"
                    @click="resendPaymentLink(payment.token)"
                >
                    Resend Payment Link
                </button>
                <button
                    type="button"
                    class="uk-button uk-button-default uk-button-small"
                    @click="edit(payment.id)"
                >
                    Edit
                </button>
            </div>
        </td>
        <td>
            <checkbox-field
                v-if="shouldDisplayCheckbox"
                :id="getIDForCheckbox(payment.id)"
                label="Deposit"
                :value="depositList.includes(payment.id)"
                @input="updateDepositList($event, payment.id)"
            />
        </td>
    </tr>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';
import { formatPrice, kebabCaseToWords, log } from "@strangeanimals/common-vue-components/src/helpers/helpers";
import { utcTimestampToLocalRelativeDate, utcTimestampToLocalPrettyDate } from "../../helpers/helpers";
const Badge = () => import("./BadgeComponent");
const CheckboxField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/CheckboxField");

export default {
    components: { CheckboxField, Badge },
    props: {
        payment: {
            type: Object,
            default: function () {
                return {};
            }
        },
        timezone: {
            type: String,
            default: 'local'
        }
    },
    computed: {
        ...mapGetters({
            depositList: 'depositList',
            isUserAdmin: 'isUserAdmin'
        }),
        amount() {
            return formatPrice(this.payment.amount);
        },
        receivedBy() {
            return this.payment.receivedBy.fullName;
        },
        shouldDisplayCheckbox() {
            return ((this.payment.paymentMethod === 'cash' || this.payment.paymentMethod === 'cheque') && this.payment.paymentStatus !== 'received');
        },
        formattedDate() {
            return utcTimestampToLocalRelativeDate(this.payment.paymentDate, this.timezone);
        },
        prettyDate() {
            return utcTimestampToLocalPrettyDate(this.payment.paymentDate, this.timezone);
        },
        paymentType() {
            return kebabCaseToWords(this.payment.paymentType);
        },
        customerFullName() {
            try {
                return this.payment.paidBy.fullName;
            }
            catch(err) {
                log('Customer full name is not set for job id: ' + this.payment.jobNumber + '.');
                return "";
            }
        },
        customerEmail() {
            try {
                return this.payment.paidBy.email;
            }
            catch(err) {
                log('Customer email is not set for job id: ' + this.payment.jobNumber + '.');
                return "";
            }
        },
        mailLinkTitle() {
            return this.customerFullName + " <" + this.customerEmail + ">";
        },
        // Previously, we can only edit in Edit page if isUserAdmin && !isCreditCardPayment.
        // We are now removing the payment method constraint.
        canEditPaymentStatus() {
            return this.isUserAdmin;
        },
    },
    methods: {
        ...mapActions({
            updateDepositListInStore: 'updateDepositList'
        }),
        edit(paymentID) {
            return this.$emit('edit', paymentID)
        },
        getReceipt(paymentID) {
            return this.$emit('getReceipt', paymentID)
        },
        resendPaymentLink(paymentToken) {
            return this.$emit('resendPaymentLink', paymentToken)
        },
        updateDepositList(checked, paymentID) {
            this.updateDepositListInStore({ add: checked, paymentID: paymentID });
        },
        getIDForCheckbox(paymentID) {
            return 'deposit-' + paymentID;
        },
        changeStatus() {
            this.$emit('changeStatus', {
                id: this.payment.id, status: this.payment.paymentStatus, jobNumber: this.payment.jobNumber, paymentType: this.payment.paymentType
            });
        }
    }
}
</script>
